import { type MaskOptions, maskInputValue, maskText } from 'ProdantixSdk/web/replay/mask';
import type { SerializedNode } from 'ProdantixSdk/web/replay/types';

export const NODE_ELEMENT = 1;
export const NODE_TEXT = 3;
export const NODE_DOCUMENT = 9;

/** A DOM attribute, abstracted so the serializer is pure + testable without a browser. */
export interface AttrLike {
  name: string;
  value: string;
}

/** A DOM node abstracted to what replay needs. The recorder adapts real `Node`s to this
 *  shape; the serializer stays pure. `inputValue`/`inputType` carry an input's live
 *  `.value` + `type` so they can be masked. */
export interface NodeLike {
  attributes?: AttrLike[];
  childNodes?: NodeLike[];
  inputType?: string;
  inputValue?: string;
  nodeType: number;
  /** Opaque back-reference to the real DOM node, carried through so the recorder can
   *  build a node→id mirror in the `visit` callback. The serializer never reads it. */
  ref?: unknown;
  tagName?: string;
  textValue?: string;
}

/** Called with every node the serializer allocates an id for, so the recorder can
 *  record a `ref → id` mirror for later incremental mutations. */
export type VisitNode = (node: NodeLike, id: number) => void;

/** Assigns stable, monotonically increasing ids as nodes are serialized. The recorder
 *  keeps one allocator across the snapshot + later mutations so ids stay stable. */
export class IdAllocator {
  private next = 1;

  allocate(): number {
    const id = this.next;
    this.next += 1;
    return id;
  }
}

const SKIP_TAGS = new Set(['NOSCRIPT', 'SCRIPT']);

/** Serializes a node tree into a `SerializedNode`, masking input values and (optionally)
 *  text. Script/noscript subtrees are dropped (never replayed). Ids are allocated
 *  pre-order (parent before children). Returns null for a skipped node. */
export function serializeNode(node: NodeLike, allocator: IdAllocator, options: MaskOptions, visit?: VisitNode): SerializedNode | null {
  if (node.nodeType === NODE_TEXT) {
    const id = allocator.allocate();
    visit?.(node, id);
    return {
      id,
      textContent: maskText(node.textValue ?? '', options),
      type: 'text',
    };
  }
  if (node.nodeType === NODE_DOCUMENT) {
    const id = allocator.allocate();
    visit?.(node, id);
    return {
      childNodes: serializeChildren(node, allocator, options, visit),
      id,
      type: 'document',
    };
  }
  if (node.nodeType === NODE_ELEMENT) {
    const tagName = node.tagName ?? '';
    if (SKIP_TAGS.has(tagName)) {
      return null;
    }
    const id = allocator.allocate();
    visit?.(node, id);
    return {
      attributes: elementAttributes(node, options),
      childNodes: serializeChildren(node, allocator, options, visit),
      id,
      tagName,
      type: 'element',
    };
  }
  return null;
}

function serializeChildren(node: NodeLike, allocator: IdAllocator, options: MaskOptions, visit?: VisitNode): SerializedNode[] {
  const children: SerializedNode[] = [];
  for (const child of node.childNodes ?? []) {
    const serialized = serializeNode(child, allocator, options, visit);
    if (serialized) {
      children.push(serialized);
    }
  }
  return children;
}

function elementAttributes(node: NodeLike, options: MaskOptions): Record<string, string> {
  const attributes: Record<string, string> = {};
  for (const attr of node.attributes ?? []) {
    attributes[attr.name] = attr.value;
  }
  if (node.inputValue !== undefined) {
    attributes.value = maskInputValue(node.inputValue, node.inputType, options);
  }
  return attributes;
}
